import { categoryColor } from './CategoryTag';

/**
 * A rosca de gastos por categoria.
 *
 * Desenhada a mao em SVG, sem biblioteca de grafico. Nao e purismo: o app
 * roda numa VPS de 2 nucleos compartilhada com outros dois sistemas, e a
 * biblioteca mais leve do mercado custaria mais bytes no navegador do que
 * todo o resto da tela junta — para desenhar um circulo.
 *
 * O truque e um so: um circulo com `stroke-dasharray` por fatia, cada um
 * girado pelo tanto que os anteriores ja ocuparam. Sem trigonometria, sem
 * caminhos calculados, e a transicao entre meses sai suave de graca.
 *
 * Acessibilidade nao e enfeite aqui: cada fatia e um `button` de verdade,
 * alcancavel por teclado, com o nome e o valor no rotulo. Uma rosca em que
 * so o mouse chega e uma rosca que metade das pessoas nao usa — e cor
 * sozinha nunca identifica nada, que e por que a legenda ao lado repete
 * tudo em texto.
 */

export interface FatiaDaRosca {
  key: string;
  label: string;
  colorToken: string | null;
  /** Em centavos, como texto. */
  amountCents: string;
  share: number;
}

const RAIO = 60;
const CIRCUNFERENCIA = 2 * Math.PI * RAIO;
const ESPESSURA = 26;

export function Rosca({
  fatias,
  selecionada,
  onSelecionar,
  centro,
  rotuloDoCentro,
}: {
  fatias: FatiaDaRosca[];
  selecionada: string | null;
  onSelecionar: (key: string | null) => void;
  /** O numero grande no meio. */
  centro: string;
  rotuloDoCentro: string;
}) {
  if (fatias.length === 0) {
    return <p className="muted">Nenhuma saida neste mes.</p>;
  }

  let percorrido = 0;

  return (
    <div className="rosca-wrap">
      <svg viewBox="0 0 160 160" className="rosca" role="img" aria-label={rotuloDoCentro}>
        {/* O anel de fundo evita um buraco quando as fatias nao fecham 100%
            por arredondamento. */}
        <circle
          cx="80"
          cy="80"
          r={RAIO}
          fill="none"
          stroke="var(--color-bg-sunken)"
          strokeWidth={ESPESSURA}
        />

        {fatias.map((fatia) => {
          const comprimento = (fatia.share / 100) * CIRCUNFERENCIA;
          const deslocamento = -(percorrido / 100) * CIRCUNFERENCIA;
          percorrido += fatia.share;

          const ativa = selecionada === null || selecionada === fatia.key;

          return (
            <circle
              key={fatia.key}
              cx="80"
              cy="80"
              r={RAIO}
              fill="none"
              stroke={categoryColor(fatia.colorToken)}
              strokeWidth={selecionada === fatia.key ? ESPESSURA + 6 : ESPESSURA}
              strokeDasharray={`${comprimento} ${CIRCUNFERENCIA - comprimento}`}
              strokeDashoffset={deslocamento}
              // Comeca no topo, e nao as tres horas.
              transform="rotate(-90 80 80)"
              opacity={ativa ? 1 : 0.28}
              style={{ transition: 'stroke-width 120ms, opacity 120ms' }}
            />
          );
        })}

        <text x="80" y="76" textAnchor="middle" className="rosca-valor">
          {centro}
        </text>
        <text x="80" y="94" textAnchor="middle" className="rosca-rotulo">
          {rotuloDoCentro}
        </text>
      </svg>

      <ul className="rosca-legenda">
        {fatias.map((fatia) => {
          const ativa = selecionada === fatia.key;
          return (
            <li key={fatia.key}>
              <button
                type="button"
                className={`rosca-item${ativa ? ' ativa' : ''}`}
                aria-pressed={ativa}
                onClick={() => onSelecionar(ativa ? null : fatia.key)}
              >
                <span
                  className="cat-dot"
                  style={{ background: categoryColor(fatia.colorToken) }}
                  aria-hidden
                />
                <span className="rosca-nome">{fatia.label}</span>
                <span className="rosca-share">{fatia.share.toFixed(0)}%</span>
                <span className="money tabular">{formatar(fatia.amountCents)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function formatar(centavos: string): string {
  return (Number(BigInt(centavos)) / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}
